/**
 * examples-logic.ts — pure (DOM-free) logic extracted from examples.ts.
 *
 * Seam extraction for ts-coverage-260626: these helpers were trapped inside the
 * IIFE-wrapped examples.ts module which reads window.* at top level, preventing
 * direct ESM import and v8 coverage. Moving them here makes them directly
 * importable and unit-testable via vitest.
 *
 * All exports are PURE (input → value/string, NO document/window/fetch deps).
 * examples.ts re-imports from this module.
 *
 * DO NOT add DOM imports, window reads, or fetch calls — that re-traps the logic.
 * DOM-touching functions (renderSampleCard, renderExpandedArticle, wireAwaitingFilters,
 * loadCatalog, boot, fetchAllSampleChanges, handleChangesClick) remain in examples.ts.
 *
 * Epic: docs/epics/active/ts-coverage-260626/main.md
 */
// ─── AWAITING_ITEMS_BY_FIXTURE ────────────────────────────────────────────────
//
// Per-fixture hardcoded awaiting-items data. Extracted from examples.ts IIFE
// so tests can assert the constant's structure without booting the full page.
// When the standards-bridge (docs/design/standards-bridge-260513.md) wires real
// data through the catalog envelope, this map becomes the fallback only.
export const AWAITING_ITEMS_BY_FIXTURE = {
    "stanford-linear-programming": [
        {
            loc: "Slide 1", category: "Grouping",
            standards: ["WCAG 1.1.1", "OFFICE-PPTX"],
            group: "Structure & grouping",
            description: 'A group shape contains 4 sub-shapes with text labels ("x₁ axis", "x₂ axis", "Optimal vertex", "Feasible region"), but no group-level alternative text is present.',
            reason: "We generated alt text for the group's visual content, but cannot determine whether the author intends the group as a semantic unit (one combined description) or as four separate independently-readable labels. If semantic: add a concise group-level alt text and mark sub-shapes as decorative. If independent: remove the grouping and ensure each shape has its own accessible name.",
        },
        {
            loc: "Slide 2", category: "Table headers",
            standards: ["WCAG 1.3.1", "MATTERHORN-15-005"],
            group: "Structure & grouping",
            description: 'The sensitivity-analysis results table has 3 column headers ("Variable", "Reduced Cost", "Range") but no row headers. The leftmost column lists variable names (x₁, x₂, s₁, s₂).',
            reason: 'If the table is two-dimensional (rows and columns both carry semantic meaning), the leftmost column should be marked as row headers (scope="row"). If the table is column-only (rows are data, not category labels), no change is needed. Only the author can determine the intended table semantics.',
        },
        {
            loc: "Slide 2", category: "Use of color",
            standards: ["WCAG 1.4.1"],
            group: "Color & contrast",
            description: "The 3-series convergence line graph encodes data series using only color (red, green, blue lines) with no redundant visual channel such as line style (dashed, dotted) or marker shape.",
            reason: "WCAG 2.2 Success Criterion 1.4.1 (Use of Color) requires that color not be the sole means of conveying information. Adding distinct line styles (solid / dashed / dotted) or per-series point markers would satisfy the criterion. DocAble cannot modify chart rendering; this requires editing the source chart in PowerPoint.",
        },
        {
            loc: "Slide 1", category: "Image interpretation",
            standards: ["WCAG 1.1.1"],
            group: "Content ambiguity",
            description: "Slide 1 contains a hand-annotated pencil sketch in the lower-right corner. The sketch shows what appears to be a coordinate plane with hand-drawn constraint lines, but the vision model returned a low-confidence classification (confidence 0.41 on a 0–1 scale).",
            reason: "DocAble's threshold for auto-applying generated alt text is 0.70 confidence. The sketch fell below this threshold; the tool declined to apply the auto-generated description rather than risk a hallucinated alt text.",
        },
        {
            loc: "Slides 1–2", category: "Artifact status",
            standards: ["WCAG 1.3.1", "MATTERHORN-1-001", "OFFICE-PPTX"],
            group: "Navigation & structure",
            description: 'Slide footer shapes contain page number text ("1/2", "2/2") and a presenter-credit line. These are not marked as artifacts and will be announced by screen readers on each slide.',
            reason: "Whether footer text is informational (should be read aloud on each slide) or decorative (an artifact, should be skipped) depends on authorial intent. If the audience is expected to follow along with a printed handout, page numbers may be useful. If the slides are self-contained, marking footers as artifacts reduces screen-reader noise. DocAble does not make this call automatically.",
        },
        {
            loc: "Slide 2", category: "Linked content",
            standards: ["WCAG 2.4.4", "WCAG 1.1.1"],
            group: "Navigation & structure",
            description: "Speaker notes on slide 2 contain a hyperlink to an external supplementary slide deck hosted on Stanford Course Hub (accessible only to enrolled students). The linked deck's accessibility status is unknown.",
            reason: "DocAble cannot access or remediate external documents. If the linked deck is required course material, it should be remediated separately and re-linked, or its content should be incorporated directly into this file.",
        },
        {
            // Demonstrates the Q2 sole-source filter: this item carries
            // BOTH a WCAG external claim AND a DocAble-proprietary claim,
            // and the renderer will show ONLY the WCAG badge (DocAble
            // suppressed). The data still carries both so future audits /
            // analytics see the complete claim set.
            loc: "Slide 1", category: "Image resolution",
            standards: ["WCAG 1.1.1", "DocAble:ImageBandwidth"],
            group: "Content ambiguity",
            description: "A 4096×3072 photograph of a whiteboard derivation is embedded at full resolution. The slide will render fine on a workstation, but accessing the file on a Chromebook over a slow connection takes noticeably longer to load.",
            reason: "DocAble auto-downsamples oversized embedded images during remediation (proprietary rule), but the source resolution here is intentional in case the author wants high-resolution print. Author review confirms whether the print path matters.",
        },
    ],
    "unc-accessible-syllabus": [
        {
            loc: "Page 1", category: "Heading hierarchy",
            standards: ["WCAG 1.3.1", "OFFICE-DOCX"],
            group: "Structure & grouping",
            description: 'The syllabus uses styled paragraphs ("Schedule", "Policies", "Grading") that look like headings but are formatted as regular paragraphs with bold + larger font, not using the Word Heading 2 style.',
            reason: "Promoting styled paragraphs to Heading 2 would change document structure in a way the author may not intend. Verify the section labels are meant to be navigable headings, then apply the Heading 2 style in Word.",
        },
        {
            loc: "Page 2", category: "Table headers",
            standards: ["WCAG 1.3.1"],
            group: "Structure & grouping",
            description: "The course-schedule table has no explicit header row marked. Without header markup, screen readers cannot associate cells with column meaning.",
            reason: "Marking the first row as a header row is a one-click change in Word but requires author judgment about whether the first row is genuinely a header (versus a data row that happens to be styled differently).",
        },
    ],
    "sjsu-grade-template": [
        {
            loc: "Sheet 'Grades'", category: "Header row",
            standards: ["WCAG 1.3.1", "OFFICE-XLSX"],
            group: "Structure & grouping",
            description: "The Grades sheet has labels in row 1 but no Excel table or named range marks them as a header row. Screen readers cannot associate data cells with their column headers.",
            reason: "Convert the data range to an Excel Table (Insert → Table, check 'My table has headers'). This is a one-step change but requires confirming the row-1 labels are headers, not display text.",
        },
        {
            loc: "Sheet 'Summary'", category: "Merged cells",
            standards: ["WCAG 1.3.1", "OFFICE-XLSX"],
            group: "Structure & grouping",
            description: "The Summary sheet uses merged cells across rows 1-3 for a banner. Merged cells disrupt navigation and screen-reader announcement order.",
            reason: "Unmerging requires repositioning the banner text — author judgment is needed for the visual replacement (e.g., increased row height, centered text in row 1 only).",
        },
    ],
    "mit-ocw-8513-quantum-lec3": [
        {
            loc: "Page 1", category: "Equation transcription",
            standards: ["WCAG 1.1.1", "MATTERHORN-09-001", "PDFUA-7.5"],
            group: "Content ambiguity",
            description: "Handwritten equations in this lecture are OCR'd to alt text. The vision model returned medium confidence (0.58) on the eigenvalue equation in the middle of page 1.",
            reason: "DocAble's threshold for auto-applying generated alt text is 0.70 confidence. Review the OCR'd LaTeX in the auto-changelog and apply manually if correct.",
        },
        {
            loc: "Page 2", category: "Mathematical notation",
            standards: ["WCAG 1.1.1", "PDFUA-7.5"],
            group: "Content ambiguity",
            description: "Several lines on page 2 use shorthand notation (overbar, hat, tilde) that may have non-canonical mathematical meaning specific to the course conventions.",
            reason: "Without the lecture context, the model cannot disambiguate course-specific notation. Author review confirms the alt text matches the intended mathematical meaning.",
        },
    ],
    "mit-ocw-6046j-lec07": [
        {
            loc: "Page 1", category: "Reading order",
            standards: ["WCAG 1.3.2", "PDFUA-7.1", "MATTERHORN-09-004"],
            group: "Navigation & structure",
            description: "The recurrence diagram on page 1 has annotations placed around the diagram with reading order ambiguous between top-to-bottom and left-to-right.",
            reason: "Reading-order corrections for spatial diagrams require author judgment about whether annotations are meant as a sequence or as parallel labels.",
        },
    ],
    "arxiv-2208.03516": [
        {
            loc: "Page 3", category: "Figure interpretation",
            standards: ["WCAG 1.1.1", "PDFUA-7.5"],
            group: "Content ambiguity",
            description: "Figure 2 (network architecture diagram) returned low-confidence alt text (0.52) because the diagram references variable names defined elsewhere in the paper.",
            reason: "Author review can verify the auto-generated description references the correct variables and architectural components.",
        },
        {
            loc: "Page 7", category: "Table headers",
            standards: ["WCAG 1.3.1", "PDFUA-7.5"],
            group: "Structure & grouping",
            description: "Table 3 (benchmark results) has 4 column headers but no row-scope headers. The leftmost column lists method names.",
            reason: "Whether the method-name column should be row headers depends on the table's intended 2D semantics. Author can confirm by inspecting the surrounding text references to the table.",
        },
    ],
};
// ─── Kind grouping (SSOT-derived) ───────────────────────────────────────────────
//
// /examples groups its samples BY DOCUMENT KIND. Rather than hand-roll the
// format→kind map + the display order (the old FORMAT_RANK hand-table), we DERIVE
// both from the advertised-formats SSOT (system-models/supported_filetypes.py →
// generated/advertised-formats.ts), so adding a format or flipping its kind
// updates /examples automatically — the join is held by import, the UNIFY rung
// (A.24). "typesetting" is a page-local FIFTH group (last) that hosts source
// formats never advertised through the catalog (LaTeX, Typst); it is PENDING the
// drive-latex-typst Epic's SSOT `DocumentKind` widening (user G-1 ruling) — when
// that lands this special case becomes a delete-the-branch, not a redesign.
import { ADVERTISED_FORMATS, ADVERTISED_BY_KIND } from './generated/advertised-formats.js';
// format (lower-cased) → SSOT DocumentKind, built once at module load.
const _KIND_BY_FORMAT = new Map(ADVERTISED_FORMATS.map((f) => [f.formatName.toLowerCase(), f.kind]));
// kind → display label, inheriting the SSOT labels; "typesetting" is page-local.
const _LABEL_BY_KIND = new Map([
    ...ADVERTISED_BY_KIND.map((g) => [g.kind, g.label]),
    ["typesetting", "Typesetting"],
]);
/**
 * Map a sample's `format` string to its display kind. Derived from the SSOT
 * (ADVERTISED_FORMATS); a format absent from the SSOT (e.g. never-advertised
 * source formats like typst) falls to the page-local "typesetting" group.
 */
export function kindForFormat(format) {
    return _KIND_BY_FORMAT.get((format ?? "").toLowerCase()) ?? "typesetting";
}
/**
 * The kind display order: the four SSOT kinds in their canonical
 * ADVERTISED_BY_KIND order (documents, presentations, spreadsheets, pdf), then
 * the page-local "typesetting" group LAST.
 */
export const KIND_ORDER = Object.freeze([
    ...ADVERTISED_BY_KIND.map((g) => g.kind),
    "typesetting",
]);
/** Human label for a kind (SSOT-inherited for the four DocumentKinds). */
export function kindLabel(kind) {
    return _LABEL_BY_KIND.get(kind) ?? kind;
}
/**
 * Group sample entries by document kind, in KIND_ORDER. Only NON-EMPTY groups
 * are returned (empty-kind rule: a kind with zero samples renders nothing).
 * Stable within each group (input order preserved) — replaces the old
 * sortSamplesByFormat flat sort with a kind-derived grouping.
 *
 * @param samples - catalog sample entries (each may carry `format`)
 * @returns non-empty kind groups in canonical order (input is not mutated)
 */
export function groupSamplesByKind(samples) {
    const byKind = new Map();
    for (const s of samples) {
        const k = kindForFormat(s.format ?? "");
        let bucket = byKind.get(k);
        if (!bucket) {
            bucket = [];
            byKind.set(k, bucket);
        }
        bucket.push(s);
    }
    const groups = [];
    for (const kind of KIND_ORDER) {
        const bucket = byKind.get(kind);
        if (bucket && bucket.length > 0) {
            groups.push({ kind, label: kindLabel(kind), samples: bucket });
        }
    }
    return groups;
}
// ─── Defect delta percentage ───────────────────────────────────────────────────
/**
 * Compute the integer percentage reduction in defect count.
 * Returns null when either argument is null/undefined or defBefore is 0
 * (avoid division by zero / misleading "+Infinity%" for 0→N regressions).
 *
 * Positive result = improvement (defects fell).
 * Negative result = regression (defects rose).
 *
 * @param defBefore - defect count before remediation
 * @param defAfter  - defect count after remediation
 */
export function computeDefectDeltaPct(defBefore, defAfter) {
    if (defBefore == null || defAfter == null)
        return null;
    const before = Number(defBefore);
    const after = Number(defAfter);
    if (!isFinite(before) || !isFinite(after))
        return null;
    if (before === 0)
        return null;
    return Math.round(((before - after) / before) * 100);
}
// ─── Awaiting-filter item match ───────────────────────────────────────────────
/**
 * Return true when any of the activeFilters strings is included in the
 * comma-separated itemStandards string (the data-standards attribute value).
 *
 * This is the pure predicate extracted from applyAwaitingFilters so it can be
 * unit-tested without real DOM NodeLists.
 *
 * @param itemStandards - comma-separated standards string from data-standards attr, e.g. "WCAG 1.1.1,OFFICE-PPTX"
 * @param activeFilters - currently active filter chips, e.g. ["WCAG 1.1.1"]
 */
export function awaitingItemMatchesFilters(itemStandards, activeFilters) {
    if (activeFilters.length === 0)
        return true;
    const stds = itemStandards.split(",").map((s) => s.trim()).filter(Boolean);
    return activeFilters.some((a) => stds.includes(a));
}
